/**
 * The email client's record rules (piece 32): the mailbox and folder kinds,
 * the seven system folders and the special-use attribute each maps onto, and
 * the enums the Message row stores at first sight.
 */
import { MODULES, type Module } from '../permissions/modules';
import type { StatusDefinitionRow } from '../statusDefinitions';

/** Whose a mailbox is (decision 0790). */
export const MAILBOX_KINDS = ['Personal', 'Department', 'Module'] as const;
export type MailboxKindValue = (typeof MAILBOX_KINDS)[number];

/** 🔒 Whether this client writes to a mailbox's host (decision 1206): WatchOnly writes nothing, and a new row starts there. */
export const MAILBOX_HOST_MODES = ['WatchOnly', 'Full'] as const;
export type MailboxHostModeValue = (typeof MAILBOX_HOST_MODES)[number];

/** The seven system folders and a person's own (decision 0082). */
export const FOLDER_KINDS = ['Inbox', 'Sent', 'Drafts', 'Trash', 'Quarantine', 'Archive', 'Outbox', 'Custom'] as const;
export type FolderKindValue = (typeof FOLDER_KINDS)[number];

/**
 * 🔨 The seven that cannot be renamed or deleted, in the order the folder tree lists them: the mainstream clients'
 * Inbox, Drafts, Sent, then Outbox, Quarantine where they read Spam, Trash and Archive (T354; decision 2640).
 */
export const SYSTEM_FOLDER_KINDS = ['Inbox', 'Drafts', 'Sent', 'Outbox', 'Quarantine', 'Trash', 'Archive'] as const satisfies readonly FolderKindValue[];
export type SystemFolderKind = (typeof SYSTEM_FOLDER_KINDS)[number];

export function isSystemFolderKind(kind: string): kind is SystemFolderKind {
  return (SYSTEM_FOLDER_KINDS as readonly string[]).includes(kind);
}

/**
 * 🔒 Six system folders map onto an IMAP special-use attribute (RFC 6154), and
 * Quarantine is `\Junk`. Outbox has none: it is local to this client, since a
 * message waiting out its undo delay has not reached the mailbox yet.
 */
export const SPECIAL_USE: Readonly<Record<Exclude<SystemFolderKind, 'Outbox'>, string>> = {
  Inbox: '\\Inbox',
  Sent: '\\Sent',
  Drafts: '\\Drafts',
  Trash: '\\Trash',
  Quarantine: '\\Junk',
  Archive: '\\Archive',
};

/** The folder kind a host folder's special-use attribute names, or Custom; IMAP names the inbox INBOX with no attribute at all. */
export function folderKindFor(hostPath: string, specialUse: readonly string[]): FolderKindValue {
  if (hostPath.toUpperCase() === 'INBOX') return 'Inbox';
  for (const [kind, attribute] of Object.entries(SPECIAL_USE)) {
    if (specialUse.some((flag) => flag.toLowerCase() === attribute.toLowerCase())) return kind as FolderKindValue;
  }
  return 'Custom';
}

export const MESSAGE_PRIORITIES = ['Normal', 'High'] as const;
export type MessagePriorityValue = (typeof MESSAGE_PRIORITIES)[number];
export const TRANSPORT_SECURITIES = ['None', 'Opportunistic', 'Verified'] as const;
export type TransportSecurityValue = (typeof TRANSPORT_SECURITIES)[number];
export const AUTH_RESULTS = ['Pass', 'Fail', 'SoftFail', 'Neutral', 'TempError', 'None'] as const;
export type AuthResultValue = (typeof AUTH_RESULTS)[number];
// Decision 1201: Invalid, a signature that does not verify over the body it signs.
export const SIGNATURE_STATUSES = ['Valid', 'Expired', 'Untrusted', 'Unsigned', 'Invalid'] as const;
export type SignatureStatusValue = (typeof SIGNATURE_STATUSES)[number];
export const QUARANTINE_REASONS = ['SecurityScreening', 'UserReported'] as const;
export type QuarantineReasonValue = (typeof QUARANTINE_REASONS)[number];
export const DELIVERY_STATUSES = ['Held', 'Sent', 'Delivered', 'Bounced', 'Deferred'] as const;
export type DeliveryStatusValue = (typeof DELIVERY_STATUSES)[number];

/**
 * 🔒 One seeded row per member of the two Message enums the header renders
 * (piece 32, Reading pane): Verified and Valid to good, Opportunistic and
 * Expired to warning, Untrusted and Invalid to error, None and Unsigned to inactive.
 */
export const MAIL_STATUS_DEFINITIONS: readonly StatusDefinitionRow[] = [
  { entityType: 'TransportSecurity', statusValue: 'Verified', label: 'TLS Verified', colorGroup: 'good', sortOrder: 1 },
  { entityType: 'TransportSecurity', statusValue: 'Opportunistic', label: 'TLS Opportunistic', colorGroup: 'warning', sortOrder: 2 },
  { entityType: 'TransportSecurity', statusValue: 'None', label: 'No TLS', colorGroup: 'inactive', sortOrder: 3 },
  { entityType: 'SignatureStatus', statusValue: 'Valid', label: 'Signature Valid', colorGroup: 'good', sortOrder: 1 },
  { entityType: 'SignatureStatus', statusValue: 'Expired', label: 'Signature Expired', colorGroup: 'warning', sortOrder: 2 },
  { entityType: 'SignatureStatus', statusValue: 'Untrusted', label: 'Signature Untrusted', colorGroup: 'error', sortOrder: 3 },
  { entityType: 'SignatureStatus', statusValue: 'Unsigned', label: 'Unsigned', colorGroup: 'inactive', sortOrder: 4 },
  // Decision 1201: a body changed after signing, or a signature no key verifies. Seeded after Unsigned, so an installed database's rows keep their order.
  { entityType: 'SignatureStatus', statusValue: 'Invalid', label: 'Signature Invalid', colorGroup: 'error', sortOrder: 5 },
  // Decision 1226: what the host said about one recipient of a sent message, drawn on the Delivered panel.
  { entityType: 'RecipientDeliveryStatus', statusValue: 'Accepted', label: 'Accepted', colorGroup: 'good', sortOrder: 1 },
  { entityType: 'RecipientDeliveryStatus', statusValue: 'Refused', label: 'Refused', colorGroup: 'error', sortOrder: 2 },
  { entityType: 'RecipientDeliveryStatus', statusValue: 'Bounced', label: 'Bounced', colorGroup: 'error', sortOrder: 3 },
];

/** A Module mailbox's key is a permission Module, never a navigation group (decision 0790). */
export function isMailboxModuleKey(value: string): value is Module {
  return (MODULES as readonly string[]).includes(value);
}

/** An address as a mailbox stores and compares it: trimmed and lower-cased. */
export function normalizeAddress(address: string): string {
  return address.trim().toLowerCase();
}

/** The routes the client answers at (piece 32; piece 12's COMMUNICATION row and the quarantine queue). */
export const MAIL_ROUTE = '/mail';
export const MAIL_QUARANTINE_ROUTE = '/mail/quarantine';
export const MAIL_QUARANTINE_CAPABILITY = 'mail.quarantine.review';
